package com.evoapps.evoshift.domain.usecase.roster

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.legal.DayClosure
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DailyRecordIds
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.schedule.CopySource
import com.evoapps.evoshift.domain.schedule.LabourCodeCheck
import com.evoapps.evoshift.domain.schedule.LabourProfile
import com.evoapps.evoshift.domain.schedule.RosterCopyMapping
import com.evoapps.evoshift.domain.usecase.GetDayClosuresUseCase
import com.evoapps.evoshift.domain.usecase.schedule.ApplyAutoScheduleUseCase
import com.evoapps.evoshift.domain.util.overlaps
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/** SH-114: why a copied shift lost its person (it is copied unassigned). Facts: the UI words them. */
enum class CopyOpenReason {
    /** The person no longer works here (engagement ended or inactive) on that day. */
    INACTIVE,
    /** An approved absence (sick leave, vacation, …) covers the day. */
    ABSENT,
    /** They marked the day unavailable. */
    UNAVAILABLE,
    /** They already work then: here, in another copied shift, or at another workplace. */
    DOUBLE_BOOKED
}

/**
 * SH-199: what the copy does on target days that already have shifts. One choice for the whole
 * copy, shown only when some target day has shifts.
 */
enum class PlannedDaysMode {
    /** Leave those days as they are (nothing is copied there). The default. */
    SKIP,
    /** Keep their shifts and add the copies (a person already working then is left out of the copy). */
    ADD,
    /** Remove their shifts, then copy; shifts with clock-ins or in a swap stay ([KeptShiftReason]). */
    REPLACE
}

/** SH-199: why a shift of a replaced day stays. Facts: the UI words them. */
enum class KeptShiftReason {
    /** Someone clocked in or out on it (actual times, breaks): a record of work, never deleted. */
    CLOCKED,
    /** It is in a marketplace swap or give-away that is not decided yet. */
    IN_SWAP
}

/** One shift the copy would create; [shift] has no id yet. */
data class CopiedShift(
    val shift: Shift,
    val sourceShiftId: String,
    /** Who worked the source shift (null: it was unassigned there too). */
    val sourceUserId: String?,
    /** Why the person was not kept; null when kept (or the source had nobody). */
    val leftOpen: CopyOpenReason? = null,
    /** SH-112: kept, but the month with it breaks a labour-code rule (marked on the grid). */
    val labourWarning: Boolean = false
)

/** SH-114: what "Copy from…" would do, shown before anything is written. */
data class CopyPreview(
    val rosterId: String,
    val source: CopySource,
    /** The roster's days the copy covers (target → source), in order. */
    val days: Map<LocalDate, LocalDate>,
    val shifts: List<CopiedShift>,
    /** Days the Sunday-trading policy or a holiday closes: their source shifts are not copied. */
    val closedDays: Map<LocalDate, DayClosure> = emptyMap(),
    /** Source shifts not copied because their day is closed. */
    val skippedClosed: Int = 0,
    /** Days left alone because they already have shifts. */
    val plannedDays: Set<LocalDate> = emptySet(),
    /** Source shifts not copied because their day already has shifts. */
    val skippedPlanned: Int = 0,
    /** SH-171: pasted days past the roster's month (or before it): not copied, never spilled into another month. */
    val outsideMonth: List<LocalDate> = emptyList(),
    /** SH-199: how days that already have shifts were treated. */
    val mode: PlannedDaysMode = PlannedDaysMode.SKIP,
    /**
     * SH-199: the target days that would receive copies and already have shifts, with how many
     * (whatever [mode]); empty means the choice need not be offered.
     */
    val daysWithShifts: Map<LocalDate, Int> = emptyMap(),
    /** SH-199 (Replace): the existing shifts the copy removes, as stored (Undo puts them back). */
    val replaced: List<Shift> = emptyList(),
    /** SH-199 (Replace): shifts of the replaced days that stay, and why. */
    val kept: Map<String, KeptShiftReason> = emptyMap(),
    /** SH-199 (Replace): the days of [kept] (shift id -> day). */
    private val keptDays: Map<String, LocalDate> = emptyMap()
) {
    /** SH-199: the shifts on the target days that already have shifts (the counts of the choice). */
    val existingShifts: Int get() = daysWithShifts.values.sum()
    /** SH-199: kept shifts by why. */
    val keptByReason: Map<KeptShiftReason, Int> get() = kept.values.groupingBy { it }.eachCount()

    /** Copies keeping their person. */
    val assigned: Int get() = shifts.count { it.shift.userId != null }
    /** Copies unassigned in the source too. */
    val unassignedInSource: Int get() = shifts.count { it.sourceUserId == null }
    /** Copies that lost their person, by why. */
    val leftOpen: Map<CopyOpenReason, Int> get() = shifts.mapNotNull { it.leftOpen }.groupingBy { it }.eachCount()
    val leftOpenTotal: Int get() = shifts.count { it.leftOpen != null }
    val labourWarnings: Int get() = shifts.count { it.labourWarning }

    /** SH-171: what the copy does to each of its target days ([days]' keys), in order. */
    val byDay: Map<LocalDate, DayCopyOutcome>
        get() {
            val copies = shifts.groupBy { LocalDate.parse(it.shift.date) }
            return days.keys.sorted().associateWith { day ->
                val closure = closedDays[day]
                val theirs = copies[day].orEmpty()
                when {
                    closure != null -> DayCopyOutcome.SkippedClosed(closure)
                    day in plannedDays -> DayCopyOutcome.SkippedPlanned
                    theirs.isEmpty() -> DayCopyOutcome.NothingToCopy
                    else -> DayCopyOutcome.Copied(
                        shifts = theirs.size,
                        withPerson = theirs.count { it.shift.userId != null },
                        leftOpen = theirs.mapNotNull { it.leftOpen }.groupingBy { it }.eachCount(),
                        unassignedInSource = theirs.count { it.sourceUserId == null },
                        labourWarnings = theirs.count { it.labourWarning },
                        replaces = replaced.count { it.date == day.toString() },
                        kept = keptDays.filterValues { it == day }.keys.mapNotNull { kept[it] }.groupingBy { it }.eachCount(),
                        alongside = if (mode == PlannedDaysMode.ADD) daysWithShifts[day] ?: 0 else 0
                    )
                }
            }
        }
}

/** SH-171: what a copy does to one target day (facts: the UI words them). */
sealed interface DayCopyOutcome {
    /** [shifts] copies: [withPerson] keep their person, the rest lost theirs ([leftOpen], by why) or had none. */
    data class Copied(
        val shifts: Int,
        val withPerson: Int,
        val leftOpen: Map<CopyOpenReason, Int> = emptyMap(),
        val unassignedInSource: Int = 0,
        val labourWarnings: Int = 0,
        /** SH-199 (Replace): the day's existing shifts removed first. */
        val replaces: Int = 0,
        /** SH-199 (Replace): the day's existing shifts that stay, by why. */
        val kept: Map<KeptShiftReason, Int> = emptyMap(),
        /** SH-199 (Add): the day's existing shifts the copies are added next to. */
        val alongside: Int = 0
    ) : DayCopyOutcome {
        val leftOpenTotal: Int get() = leftOpen.values.sum()
        val keptTotal: Int get() = kept.values.sum()
    }

    /** Closed by the Sunday-trading policy or a holiday: nothing copied. */
    data class SkippedClosed(val closure: DayClosure) : DayCopyOutcome

    /** It already has shifts and the copy does not add to such days. */
    data object SkippedPlanned : DayCopyOutcome

    /** The source day has no shifts. */
    data object NothingToCopy : DayCopyOutcome
}

/**
 * SH-114: "Copy from…" in the roster builder: last week, a chosen week or last month (by
 * weekday, [RosterCopyMapping]) into the roster, people kept. SH-171: also a block of days pasted
 * from one or more start days ([CopySource.Days], "Copy days"; one day onto several days is its
 * simplest case), with the same rules; pasted days outside the roster's month are not copied. [preview] reads and decides
 * everything without writing; [apply] writes the copies in one batch and returns their ids, so
 * [undo] removes exactly those.
 *
 * What no longer fits is skipped or flagged:
 * - days the workplace's Sunday policy closes (non-trading Sundays, holidays): not copied;
 * - days that already have shifts: left alone, unless the manager asks to add to them;
 * - a person who no longer works here, is absent (approved), unavailable that day or already
 *   working then (here, in another copy, at another workplace they work at): the shift is copied
 *   without them, so the soft validation of assigning is kept;
 * - the labour code (SH-112, [LabourCodeCheck]): kept, flagged, and marked on the grid.
 *
 * Copies are drafts: unpublished, `hasUnpublishedChanges`, a new version 1, no marketplace
 * offer, no clocked times, tagged [TAG].
 */
class CopyShiftsUseCase(
    private val rosterRepository: RosterRepository,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val userRepository: UserRepository,
    private val availabilityRepository: AvailabilityRepository,
    private val absenceRepository: AbsenceRepository,
    private val getDayClosures: GetDayClosuresUseCase = GetDayClosuresUseCase(),
    /** Prices the copies into cost snapshots (the month's refresh, [recalculate]); a failure never fails the copy. */
    private val calculateShiftCost: com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase? = null,
    /**
     * SH-197: the month's daily records (planned hours and labour cost, labour %) worked out again
     * from the shifts after a copy or its undo ([recalculate]), so they follow who the copies are
     * for; a failure never fails the copy (the month's next opening by a financial manager catches up).
     */
    private val regenerateDailyRecords: com.evoapps.evoshift.domain.usecase.finance.RegenerateDailyRecordsUseCase? = null
) {
    companion object {
        /** Tag on a copied shift. */
        const val TAG = "COPIED"
        /** metadata.createdBy when no editor is given. */
        const val CREATED_BY = "SYSTEM_COPY"
        private const val PREVIEW_ID_PREFIX = "copy-preview-"
    }

    /**
     * @param plannedDays SH-199: days that already have shifts are skipped (default), added to or replaced.
     * @param labourProfiles SH-112: each person's contract and norms where readable (else rest rules only).
     * @param nominalMonthlyHours full-time hours of the roster's month.
     */
    suspend fun preview(
        rosterId: String,
        source: CopySource,
        plannedDays: PlannedDaysMode = PlannedDaysMode.SKIP,
        labourProfiles: Map<String, LabourProfile> = emptyMap(),
        nominalMonthlyHours: Double = 160.0,
        editorUserId: String? = null
    ): Result<CopyPreview> = runCatching {
        val roster = rosterRepository.getRoster(rosterId).getOrThrow()
        val workplaceId = roster.workplaceId
        val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
        val days = RosterCopyMapping.days(source, roster.year, roster.month)
        val outsideMonth = RosterCopyMapping.outsideMonth(source, roster.year, roster.month)
        val existing = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        if (days.isEmpty()) return@runCatching CopyPreview(rosterId, source, days, emptyList(), outsideMonth = outsideMonth)

        // The source days' shifts, from every roster month they fall in.
        val sourceDates = days.values.map { it.toString() }.toSet()
        val sourceShifts = RosterIds.monthsBetween(sourceDates.min(), sourceDates.max())
            .map { (year, month) -> RosterIds.of(workplaceId, year, month) }
            .flatMap { id -> if (id == rosterId) existing else shiftRepository.getShiftsForRoster(id).getOrThrow() }
            .filter { it.date in sourceDates }
            .groupBy { it.date }

        val now = AppClock.now().toString()
        decide(rosterId, roster, workplace, existing, source, days, outsideMonth, plannedDays, labourProfiles, nominalMonthlyHours) { target, from ->
            val theirs = sourceShifts[from.toString()].orEmpty()
            val offset = target.toEpochDays() - from.toEpochDays()
            theirs.map { it to copyOf(it, rosterId, workplaceId, target, offset, now, editorUserId) }
        }
    }

    /**
     * SH-217: the roster import's shifts ([drafts]: a key per shift, the shift on its day with its
     * person) checked like a copy: closed days, days that already have shifts ([plannedDays]),
     * people who no longer work here, are absent, unavailable or already working then (left
     * without them), the labour code. People in [newPeople] are added to the team by the import
     * itself: they are taken as working here, and only checked against their other imported
     * shifts. The preview's [CopiedShift.sourceShiftId] is the draft's key. [apply] writes it and
     * [undo] removes it like a copy.
     */
    suspend fun previewImport(
        rosterId: String,
        drafts: List<Pair<String, Shift>>,
        plannedDays: PlannedDaysMode = PlannedDaysMode.SKIP,
        newPeople: Set<String> = emptySet(),
        labourProfiles: Map<String, LabourProfile> = emptyMap(),
        nominalMonthlyHours: Double = 160.0
    ): Result<CopyPreview> = runCatching {
        val roster = rosterRepository.getRoster(rosterId).getOrThrow()
        val workplace = workplaceRepository.getWorkplace(roster.workplaceId).getOrThrow()
        val existing = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        val byDate = drafts.groupBy { LocalDate.parse(it.second.date.take(10)) }
        val source = CopySource.Imported(byDate.keys)
        val days = RosterCopyMapping.days(source, roster.year, roster.month)
        val outsideMonth = byDate.keys.filter { it !in days }.sorted()
        if (days.isEmpty()) return@runCatching CopyPreview(rosterId, source, days, emptyList(), outsideMonth = outsideMonth)
        decide(rosterId, roster, workplace, existing, source, days, outsideMonth, plannedDays, labourProfiles, nominalMonthlyHours, newPeople) { target, _ ->
            byDate[target].orEmpty().map { (key, shift) -> shift.copy(id = key) to shift.copy(id = "", rosterId = rosterId) }
        }
    }

    /**
     * What [candidates] (the source shift and its copy, for each target day and its source day)
     * would do to the roster: the shared rules of [preview] and [previewImport].
     */
    private suspend fun decide(
        rosterId: String,
        roster: com.evoapps.evoshift.domain.model.Roster,
        workplace: com.evoapps.evoshift.domain.model.Workplace,
        existing: List<Shift>,
        source: CopySource,
        days: Map<LocalDate, LocalDate>,
        outsideMonth: List<LocalDate>,
        plannedDays: PlannedDaysMode,
        labourProfiles: Map<String, LabourProfile>,
        nominalMonthlyHours: Double,
        newPeople: Set<String> = emptySet(),
        candidatesOf: (target: LocalDate, from: LocalDate) -> List<Pair<Shift, Shift>>
    ): CopyPreview {
        val workplaceId = roster.workplaceId
        val closures = getDayClosures(workplace.settings, roster.year, roster.month)
        val existingByDate = existing.groupBy { it.date }
        var skippedClosed = 0
        var skippedPlanned = 0
        val closedDays = mutableMapOf<LocalDate, DayClosure>()
        val skippedDays = mutableSetOf<LocalDate>()
        val daysWithShifts = mutableMapOf<LocalDate, Int>()
        val replaced = mutableListOf<Shift>()
        val kept = mutableMapOf<String, KeptShiftReason>()
        val keptDays = mutableMapOf<String, LocalDate>()
        val candidates = days.entries.sortedBy { it.key }.flatMap { (target, from) ->
            val theirs = candidatesOf(target, from)
            if (theirs.isEmpty()) return@flatMap emptyList()
            closures[target]?.let { closure ->
                closedDays[target] = closure
                skippedClosed += theirs.size
                return@flatMap emptyList()
            }
            val there = existingByDate[target.toString()].orEmpty()
            if (there.isNotEmpty()) {
                daysWithShifts[target] = there.size
                when (plannedDays) {
                    PlannedDaysMode.SKIP -> {
                        skippedDays += target
                        skippedPlanned += theirs.size
                        return@flatMap emptyList()
                    }
                    PlannedDaysMode.ADD -> Unit
                    PlannedDaysMode.REPLACE -> there.forEach { shift ->
                        val stays = keptReason(shift)
                        if (stays == null) replaced += shift else {
                            kept[shift.id] = stays
                            keptDays[shift.id] = target
                        }
                    }
                }
            }
            theirs
        }.sortedBy { it.second.plannedTime.start }

        // Who still fits: the active team, approved absences, availability, other shifts.
        // SH-197: a read that fails fails the preview. Read as "nobody", a refused team read left
        // every copy without its person ("no longer works here") and refused absences let copies
        // keep people who are away.
        val people = candidates.mapNotNull { it.first.userId }.filter { it !in newPeople }.toSet()
        val team = if (people.isEmpty()) emptyMap() else
            userRepository.getAssignableEmployees(workplaceId).getOrThrow().associateBy { it.uid }
        val monthStart = LocalDate(roster.year, roster.month, 1)
        val monthEnd = monthStart.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1))
        val absences = if (people.isEmpty()) emptyList() else absenceRepository
            .getAbsencesForRoster(workplaceId, monthStart.toString(), monthEnd.toString()).getOrThrow()
            .filter { it.status == AbsenceStatus.APPROVED && it.userId in people && (it.workplaceId == null || it.workplaceId == workplaceId) }
        val availability = availabilityOf(people, team.keys, rosterId, workplaceId, roster.getMonthYear())
        val from = days.keys.min().plus(DatePeriod(days = -1)).toString()
        val to = days.keys.max().plus(DatePeriod(days = 1)).toString()
        // SH-135: other workplaces' shifts from the server (another employer's only once published).
        // A failure here keeps people (it never drops one); the grid's conflict check still flags them.
        val withOtherWorkplaces = people.filter { uid ->
            team[uid]?.engagements?.values?.any { it.isActive && it.workplaceId != workplaceId } == true
        }
        val elsewhere: Map<String, List<TimeRange>> = if (withOtherWorkplaces.isEmpty()) emptyMap() else
            shiftRepository.findBusyElsewhere(workplaceId, withOtherWorkplaces, from, to).getOrDefault(emptyList())
                .groupBy { it.userId }.mapValues { (_, busy) -> busy.map { it.plannedTime } }
        // SH-199: what stays of the month (Replace removes the replaced days' shifts first).
        val replacedIds = replaced.map { it.id }.toSet()
        val remaining = existing.filter { it.id !in replacedIds }
        val busy: MutableMap<String, MutableList<TimeRange>> = remaining.filter { it.userId != null }
            .groupBy { it.userId!! }.mapValues { (_, s) -> s.map { it.plannedTime }.toMutableList() }.toMutableMap()

        val decided = candidates.map { (original, copy) ->
            val userId = original.userId ?: return@map CopiedShift(copy, original.id, null)
            val date = copy.date
            val engagement = team[userId]?.engagements?.get(workplaceId)
            val reason = if (userId in newPeople) {
                // SH-217: someone the import adds: nothing about them yet but these shifts.
                CopyOpenReason.DOUBLE_BOOKED.takeIf { busy[userId].orEmpty().any { it.overlaps(copy.plannedTime) } }
            } else when {
                engagement == null || !engagement.isActive ||
                    (engagement.endDate?.takeIf { it.isNotBlank() }?.let { it.take(10) < date } == true) ||
                    engagement.startDate.take(10) > date -> CopyOpenReason.INACTIVE
                absences.any { it.userId == userId && it.startDate.take(10) <= date && it.endDate.take(10) >= date } -> CopyOpenReason.ABSENT
                availability[userId]?.days?.get(date.takeLast(2).toInt())?.status == AvailabilityStatus.UNAVAILABLE -> CopyOpenReason.UNAVAILABLE
                (busy[userId].orEmpty() + elsewhere[userId].orEmpty()).any { it.overlaps(copy.plannedTime) } -> CopyOpenReason.DOUBLE_BOOKED
                else -> null
            }
            if (reason != null) {
                CopiedShift(copy.copy(userId = null, userDisplayName = null), original.id, userId, leftOpen = reason)
            } else {
                busy.getOrPut(userId) { mutableListOf() } += copy.plannedTime
                val name = team[userId]?.displayName ?: copy.userDisplayName
                CopiedShift(copy.copy(userDisplayName = name), original.id, userId)
            }
        }

        // SH-112: the month with the copies, checked like the builder checks it.
        val withIds = decided.mapIndexed { i, c -> c.shift.copy(id = "$PREVIEW_ID_PREFIX$i") }
        val report = LabourCodeCheck.check(remaining + withIds, roster.year, roster.month, labourProfiles, nominalMonthlyHours)
        val flagged = decided.mapIndexed { i, c -> c.copy(labourWarning = "$PREVIEW_ID_PREFIX$i" in report.byShiftId) }

        return CopyPreview(
            rosterId = rosterId,
            source = source,
            days = days,
            shifts = flagged,
            closedDays = closedDays,
            skippedClosed = skippedClosed,
            plannedDays = skippedDays,
            skippedPlanned = skippedPlanned,
            outsideMonth = outsideMonth,
            mode = plannedDays,
            daysWithShifts = daysWithShifts,
            replaced = replaced,
            kept = kept,
            keptDays = keptDays
        )
    }

    /**
     * SH-205: how many shifts each day of the workplace's month [year]-[month] has (a month
     * without a roster has none): the copy tool's source calendar for a month other than the
     * roster's own. A refused read fails.
     */
    suspend fun shiftsPerDay(rosterId: String, year: Int, month: Int): Result<Map<LocalDate, Int>> = runCatching {
        val id = RosterIds.of(RosterIds.workplaceIdOf(rosterId), year, month)
        shiftRepository.getShiftsForRoster(id).getOrThrow()
            .groupingBy { LocalDate.parse(it.date.take(10)) }.eachCount()
    }

    /** SH-199: why [shift] stays when its day is replaced; null when it may go. */
    private fun keptReason(shift: Shift): KeptShiftReason? = when {
        shift.actualTime?.let { it.start.isNotBlank() || it.end.isNotBlank() } == true ||
            shift.breaks.isNotEmpty() || shift.clockAudit != null || shift.unplanned != null -> KeptShiftReason.CLOCKED
        shift.swapRequest?.let { it.status != SwapStatus.REJECTED } == true -> KeptShiftReason.IN_SWAP
        else -> null
    }

    /**
     * Writes the preview's copies (SH-199 Replace: and removes [CopyPreview.replaced], together
     * with them where one batch holds both); returns the copies' ids (for [undo]), in the
     * preview's order.
     *
     * SH-260: only the shifts. The month's costs and daily records follow with [recalculate],
     * which the caller runs without waiting for it (a month imported with Replace waited minutes
     * on its spinner for the pricing), in a scope that outlives the screen.
     */
    suspend fun apply(preview: CopyPreview): Result<List<String>> {
        if (preview.shifts.isEmpty()) return Result.success(emptyList())
        return if (preview.replaced.isEmpty()) shiftRepository.createShiftsWithIds(preview.shifts.map { it.shift })
            else shiftRepository.replaceShifts(preview.replaced, preview.shifts.map { it.shift })
    }

    /**
     * SH-260: after [apply] or [undo]: the month's shifts priced into cost snapshots (the copies,
     * shifts put back; removed shifts lose theirs), then SH-197's daily records (planned hours and
     * labour cost, labour %) worked out again from the shifts, so they follow who the copies are
     * for. Never undoes the copy: a failure is returned (the first one; the daily records are
     * still worked out after a failed pricing) for the caller to report, and the month's next
     * opening by a financial manager catches up.
     */
    suspend fun recalculate(rosterId: String): Result<Unit> {
        val pricing = calculateShiftCost?.refreshRoster(rosterId)?.map { }
        val records = regenerateDailyRecords?.let { regenerate ->
            val (year, month) = RosterIds.yearMonthOf(rosterId)
            regenerate(RosterIds.workplaceIdOf(rosterId), rosterId, year, month).map { }
        }
        return listOfNotNull(pricing, records).firstOrNull { it.isFailure } ?: Result.success(Unit)
    }

    /**
     * Removes the copies [shiftIds] ([apply]'s result; SH-217: also an import's shifts) and nothing else: shifts of the roster that
     * are not among them stay, and a copy published meanwhile is kept. SH-199: the shifts a Replace
     * removed ([restore], the preview's [CopyPreview.replaced]) come back exactly as they were (ids,
     * people, times, flags), unless one exists again meanwhile. Returns how many copies went.
     * SH-260: like [apply], only the shifts; [recalculate] follows.
     */
    suspend fun undo(rosterId: String, shiftIds: Collection<String>, restore: List<Shift> = emptyList()): Result<Int> = runCatching {
        val ids = shiftIds.toSet()
        val month = shiftRepository.getShiftsForRoster(rosterId).getOrThrow()
        val removable = month
            .filter { it.id in ids && !it.isPublished && (TAG in it.tags || com.evoapps.evoshift.domain.rosterimport.RosterImportPlanner.TAG in it.tags) }
            .map { it.id }
        val present = month.map { it.id }.toSet()
        val back = restore.filter { it.id !in present }
        if (removable.isNotEmpty()) shiftRepository.deleteShifts(removable).getOrThrow()
        // SH-263: back under their ids, their removal records dropped (the team's shifts are there again).
        if (back.isNotEmpty()) shiftRepository.restoreShifts(back).getOrThrow()
        removable.size
    }

    /** The person's month: submitted for this roster, else their saved month. */
    private suspend fun availabilityOf(
        people: Set<String>,
        team: Set<String>,
        rosterId: String,
        workplaceId: String,
        month: String
    ): Map<String, Availability> {
        if (people.isEmpty()) return emptyMap()
        val submitted = availabilityRepository.getAvailabilityForRoster(rosterId).getOrThrow()
            .filter { it.workplaceId == null || it.workplaceId == workplaceId }
            .associateBy { it.userId }
        return people.filter { it in team }.mapNotNull { uid ->
            // A month not filled in yet is no availability (or reads as all available); a refused read fails.
            (submitted[uid] ?: availabilityRepository.getAvailability(uid, workplaceId, month)
                .fold({ it }, { e -> if (e is NotFoundException) null else throw e }))?.let { uid to it }
        }.toMap()
    }

    /** A draft copy of [original] on [target]: same times, role, skills and person; nothing of its history. */
    private fun copyOf(
        original: Shift,
        rosterId: String,
        workplaceId: String,
        target: LocalDate,
        offsetDays: Long,
        now: String,
        editorUserId: String?
    ): Shift = Shift(
        id = "",
        rosterId = rosterId,
        date = target.toString(),
        dailyRecordId = DailyRecordIds.of(workplaceId, target.toString()),
        requiredRole = original.requiredRole,
        requiredSkills = original.requiredSkills,
        userId = original.userId,
        userDisplayName = original.userDisplayName,
        actualUserId = null,
        isPublished = false,
        hasUnpublishedChanges = true,
        version = 1,
        plannedTime = RosterCopyMapping.moved(original.plannedTime, offsetDays),
        actualTime = null,
        swapRequest = null,
        isOpen = original.isOpen && original.userId == null,
        conflictMetadata = null,
        metadata = AuditMetadata(createdAt = now, createdBy = editorUserId ?: CREATED_BY),
        absenceId = null,
        tags = (original.tags - ApplyAutoScheduleUseCase.TAG + TAG).distinct()
    )
}
